import { getLanguage as getInterfaceLanguage } from '@/utils/i18n';
import { tx as translateInterfaceText } from '@/utils/i18n';
import { useLanguage } from '@/hooks/useLanguage';
import React, { useMemo, useRef, useState } from 'react';
import { DateRange } from 'react-day-picker';
import { ArrowDown, ArrowUp, ExternalLink } from 'lucide-react';
import { TrafficRetentionChart } from '../../traffic/TrafficRetentionChart';
import { UtmSeriesPicker } from '../../traffic/UtmSeriesPicker';
import { AUTO_SERIES, MAX_SERIES, UTM_COLORS, rankUtmSeries } from '../../traffic/utils/utmSeries';
import { Card } from '@/components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

interface TrafficTabContentProps {
  date?: DateRange | undefined;
  videoId: string | null;
  metricsEnhanced?: any;
}

type UtmType = 'utm_source' | 'utm_medium' | 'utm_campaign' | 'utm_term' | 'utm_content';

interface TrafficSourceRow {
  value: string;
  views?: number;
  uniqueViews?: number;
  plays?: number;
  uniquePlays?: number;
  clicks?: number;
  engagement?: number;
  avgRetention?: number;
  // Marcador do corte de UTM do backend (METRICS_UTM_TOP_N): esta linha é a
  // cauda somada, não uma origem. `aggregatedValues` diz quantas ela agrega.
  aggregated?: boolean;
  aggregatedValues?: number;
}

interface UtmRetentionRow {
  value: string;
  retention: { [time: string]: { count: number; percent: number } };
  aggregated?: boolean;
}

type ByUtmType<T> = Record<UtmType, T[]>;

type SortKey =
  | 'value'
  | 'views'
  | 'uniqueViews'
  | 'plays'
  | 'uniquePlays'
  | 'clicks'
  | 'engagement'
  | 'avgRetention';

// Colunas numéricas da tabela, na ordem em que aparecem.
const TABLE_METRIC_COLUMNS: Array<{ key: SortKey; label: string }> = [
  { key: 'views', get label() { return translateInterfaceText("Visualizações"); } },
  { key: 'uniqueViews', get label() { return translateInterfaceText("Vis. Únicas"); } },
  { key: 'plays', get label() { return translateInterfaceText("Plays"); } },
  { key: 'uniquePlays', get label() { return translateInterfaceText("Plays Únicos"); } },
  { key: 'clicks', get label() { return translateInterfaceText("Cliques"); } },
  { key: 'engagement', get label() { return translateInterfaceText("Engajamento"); } },
  { key: 'avgRetention', get label() { return translateInterfaceText("Retenção Média"); } },
];

// Cabeçalho clicável: primeiro clique traz os maiores, o segundo inverte.
const SortableTh = ({
  label,
  sortKey,
  sortBy,
  sortDir,
  align = 'right',
  onClick,
}: {
  label: string;
  sortKey: SortKey;
  sortBy: SortKey;
  sortDir: 'asc' | 'desc';
  align?: 'left' | 'right';
  onClick: (key: SortKey) => void;
}) => {
  useLanguage();
  const active = sortBy === sortKey;

  return (
    <th
      className={`sticky top-0 z-10 bg-white shadow-[inset_0_-1px_0_#E2E8FF] py-3 px-2 text-[13px] font-semibold ${
        align === 'left' ? 'text-left' : 'text-right'
      }`}
    >
      <button
        type="button"
        onClick={() => onClick(sortKey)}
        className={`inline-flex items-center gap-1 transition-colors ${
          active ? 'text-[#2563FF]' : 'text-[#6B7280] hover:text-[#0F172A]'
        }`}
      >
        {label}
        <span className="inline-flex flex-col -space-y-[3px] ml-0.5">
          <ArrowUp
            className={`w-2.5 h-2.5 ${active && sortDir === 'asc' ? 'text-[#2563FF]' : 'text-[#CBD5E1]'}`}
            strokeWidth={3}
          />
          <ArrowDown
            className={`w-2.5 h-2.5 ${active && sortDir === 'desc' ? 'text-[#2563FF]' : 'text-[#CBD5E1]'}`}
            strokeWidth={3}
          />
        </span>
      </button>
    </th>
  );
};

// Referências fixas para o caso "sem dados": um literal novo a cada render
// invalidaria os useMemo abaixo sem necessidade.
const EMPTY_TRAFFIC_SOURCES: ByUtmType<TrafficSourceRow> = {
  utm_source: [],
  utm_medium: [],
  utm_campaign: [],
  utm_term: [],
  utm_content: []
};

const EMPTY_TRAFFIC_RETENTION: ByUtmType<UtmRetentionRow> = {
  utm_source: [],
  utm_medium: [],
  utm_campaign: [],
  utm_term: [],
  utm_content: []
};

const UTM_TYPE_LABELS: Record<UtmType, string> = {
  get utm_source() { return translateInterfaceText("Origem (Source)"); },
  get utm_medium() { return translateInterfaceText("Meio (Medium)"); },
  get utm_campaign() { return translateInterfaceText("Campanha (Campaign)"); },
  get utm_term() { return translateInterfaceText("Termo (Term)"); },
  get utm_content() { return translateInterfaceText("Conteúdo (Content)"); }
};

export function TrafficTabContent({
  date,
  videoId,
  metricsEnhanced
}: TrafficTabContentProps) {
  useLanguage();
  // Usar os dados reais do metricsEnhanced
  const isLoading = metricsEnhanced?.isLoading;
  const retentionTimeline = metricsEnhanced?.retentionTimeline || [];
  const [selectedUtmType, setSelectedUtmType] = useState<UtmType>('utm_source');

  // A seleção vale para um contexto (vídeo + período + tipo de UTM). Guardá-la
  // junto da chave é o que faz trocar de vídeo recomeçar do automático sem
  // precisar de efeito — e null continua significando "automático".
  const selectionKey = `${videoId ?? ''}|${date?.from?.toISOString() ?? ''}|${date?.to?.toISOString() ?? ''}|${selectedUtmType}`;
  const [selection, setSelection] = useState<{ key: string; values: string[] | null }>({
    key: selectionKey,
    values: null,
  });

  const selectedUtms = selection.key === selectionKey ? selection.values : null;
  const setSelectedUtms = (values: string[] | null) => setSelection({ key: selectionKey, values });

  // Cor por origem: guardamos o slot da paleta para que remover um chip do meio
  // não faça as cores das outras curvas andarem.
  const colorSlotsRef = useRef<Record<string, number>>({});
  const colorContextRef = useRef(selectionKey);

  // Contexto novo começa com a paleta zerada.
  if (colorContextRef.current !== selectionKey) {
    colorContextRef.current = selectionKey;
    colorSlotsRef.current = {};
  }

  // Ordenação da tabela. Abre por plays decrescente, a mesma métrica que escolhe
  // as origens do gráfico.
  const [sortBy, setSortBy] = useState<SortKey>('plays');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');

  const handleSort = (key: SortKey) => {
    if (key === sortBy) {
      setSortDir((atual) => (atual === 'desc' ? 'asc' : 'desc'));
      return;
    }

    // Coluna nova abre pelos maiores; a de texto abre em A-Z.
    setSortBy(key);
    setSortDir(key === 'value' ? 'asc' : 'desc');
  };

  const timelineData = retentionTimeline.map((point: any) => ({
    time: point.time_seconds,
    retention: point.retention_pct,
    viewers_count: point.viewers_count,
  }));

  const trafficSources: ByUtmType<TrafficSourceRow> = metricsEnhanced?.trafficSources || EMPTY_TRAFFIC_SOURCES;
  const trafficRetention: ByUtmType<UtmRetentionRow> = metricsEnhanced?.trafficRetention || EMPTY_TRAFFIC_RETENTION;

  const currentTrafficData = trafficSources[selectedUtmType] || EMPTY_TRAFFIC_SOURCES[selectedUtmType];
  const currentRetentionData = trafficRetention[selectedUtmType] || EMPTY_TRAFFIC_RETENTION[selectedUtmType];


  // Todas as origens disponíveis, da maior para a menor por plays.
  const rankedUtms = useMemo(
    () => rankUtmSeries(currentRetentionData, currentTrafficData),
    [currentRetentionData, currentTrafficData]
  );

  // Seleção automática: as mais relevantes, ignorando origens sem volume (que
  // desenhariam uma linha rente ao zero sem dizer nada) e a linha agregada — ela
  // é a soma da cauda, então ganharia de todas e não é uma origem comparável.
  const autoSelectedValues = useMemo(() => {
    const elegiveis = rankedUtms.filter((serie) => !serie.aggregated);
    const comVolume = elegiveis.filter((serie) => serie.volume > 0);
    const base = comVolume.length > 0 ? comVolume : elegiveis.length > 0 ? elegiveis : rankedUtms;
    return base.slice(0, AUTO_SERIES).map((serie) => serie.value);
  }, [rankedUtms]);

  // Devolve as séries e se elas são a escolha automática — o rodapé do gráfico
  // precisa do segundo dado porque a seleção manual pode cair no fallback abaixo.
  const utmSelection = useMemo(() => {
    const disponiveis = new Set(rankedUtms.map((serie) => serie.value));
    const escolhidas = selectedUtms ?? autoSelectedValues;
    const validas = escolhidas.filter((value) => disponiveis.has(value));

    // Um refetch pode derrubar as origens escolhidas. Nesse caso o gráfico volta
    // ao automático — "nenhuma origem selecionada" fica reservado a quem
    // realmente esvaziou a seleção.
    if (validas.length === 0 && escolhidas.length > 0) {
      return { values: autoSelectedValues.slice(0, MAX_SERIES), isAuto: true };
    }

    return { values: validas.slice(0, MAX_SERIES), isAuto: selectedUtms === null };
  }, [selectedUtms, autoSelectedValues, rankedUtms]);

  const effectiveValues = utmSelection.values;

  // Séries efetivamente desenhadas, já com rótulo curto e cor estável.
  const chartSeries = useMemo(() => {
    const slots = colorSlotsRef.current;
    const ativas = new Set(effectiveValues);

    // Devolve ao pool o slot das origens que saíram do gráfico.
    Object.keys(slots).forEach((value) => {
      if (!ativas.has(value)) delete slots[value];
    });

    const slotsEmUso = new Set(Object.values(slots));
    const retentionByValue = new Map(currentRetentionData.map((item) => [item.value, item]));
    const rankedByValue = new Map(rankedUtms.map((serie) => [serie.value, serie]));

    return effectiveValues
      .map((value) => {
        const item = retentionByValue.get(value);
        if (!item) return null;

        if (slots[value] === undefined) {
          let slot = 0;
          while (slotsEmUso.has(slot) && slot < MAX_SERIES - 1) slot++;
          slots[value] = slot;
          slotsEmUso.add(slot);
        }

        const ranked = rankedByValue.get(value);

        return {
          ...item,
          label: ranked?.label,
          volume: ranked?.volume,
          color: UTM_COLORS[slots[value] % UTM_COLORS.length],
        };
      })
      .filter(Boolean);
  }, [effectiveValues, currentRetentionData, rankedUtms]);

  // A cauda agregada não é uma origem: fora da contagem do cabeçalho.
  const originCount = useMemo(
    () => currentTrafficData.filter((source) => !source.aggregated).length,
    [currentTrafficData]
  );

  // 'Direto' é sintético: o backend agrega nele os acessos sem UTM na lista de
  // origens, mas pula esses acessos na curva de retenção.
  const temDiretoSemCurva = useMemo(
    () =>
      currentTrafficData.some((source) => source.value === 'Direto') &&
      !currentRetentionData.some((item) => item.value === 'Direto'),
    [currentTrafficData, currentRetentionData]
  );

  // A tabela é a saída completa das origens que não couberam no gráfico — o
  // backend devolve em ordem de varredura, então a ordem é sempre nossa.
  const sortedTrafficData = useMemo(() => {
    const fator = sortDir === 'desc' ? -1 : 1;

    return [...currentTrafficData].sort((a, b) => {
      // 'Outros' é a cauda somada pelo backend, não uma origem: por volume
      // ganharia de todas e por nome se perderia no meio do alfabeto. Fica
      // sempre no fim, reconhecida pelo marcador — nunca pelo texto, porque
      // 'Outros' também pode ser uma UTM legítima do cliente.
      if (!!a.aggregated !== !!b.aggregated) return a.aggregated ? 1 : -1;

      if (sortBy === 'value') {
        return (a.value || '').localeCompare(b.value || '', 'pt-BR') * fator;
      }
      return (Number(a[sortBy] || 0) - Number(b[sortBy] || 0)) * fator;
    });
  }, [currentTrafficData, sortBy, sortDir]);

  return (
    <div className="space-y-6">
      {/* Gráfico de Retenção - Full Width */}
      {timelineData && (
        <TrafficRetentionChart
          timelineData={timelineData}
          isLoading={isLoading}
          utmRetentionData={chartSeries}
          utmHasData={currentRetentionData.length > 0}
          utmTotalCount={rankedUtms.length}
          utmAutoSelection={utmSelection.isAuto}
          onRemoveSeries={(value) => setSelectedUtms(effectiveValues.filter((item) => item !== value))}
          onResetSeries={() => setSelectedUtms(null)}
          utmLegendExtra={
            rankedUtms.length > 1 ? (
              <UtmSeriesPicker
                options={rankedUtms}
                selected={effectiveValues}
                onChange={setSelectedUtms}
                onResetAuto={() => setSelectedUtms(null)}
              />
            ) : null
          }
        />
      )}

      {/* Tabela de Fontes de Tráfego */}
      <Card className="border border-[#E2E8FF] rounded-[14px] bg-white p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-[18px] font-semibold text-[#1A1A1A]">{translateInterfaceText("Fontes de Tráfego (UTM)")}</h3>
            {originCount > 0 && (
              <p className="text-[13px] text-[#6B7280] mt-0.5">
                {originCount} {originCount === 1 ? translateInterfaceText("origem detectada") : translateInterfaceText("origens detectadas")}
              </p>
            )}
          </div>

          <Select value={selectedUtmType} onValueChange={(value) => setSelectedUtmType(value as UtmType)}>
            <SelectTrigger className="w-[220px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {Object.entries(UTM_TYPE_LABELS).map(([value, label]) => (
                <SelectItem key={value} value={value}>
                  {label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {sortedTrafficData.length > 0 ? (
          <>
          {/* A lista costuma ter dezenas de origens: rola dentro da própria seção
              em vez de esticar a página inteira. No mobile o scroller é a página,
              então a tabela volta a fluir. */}
          <div className="overflow-x-auto lg:max-h-[520px] lg:overflow-y-auto metrics-scroll">
            <table className="w-full">
              <thead>
                <tr>
                  <SortableTh
                    label={UTM_TYPE_LABELS[selectedUtmType].split(' ')[0]}
                    sortKey="value"
                    align="left"
                    sortBy={sortBy}
                    sortDir={sortDir}
                    onClick={handleSort}
                  />
                  {TABLE_METRIC_COLUMNS.map((coluna) => (
                    <SortableTh
                      key={coluna.key}
                      label={coluna.label}
                      sortKey={coluna.key}
                      sortBy={sortBy}
                      sortDir={sortDir}
                      onClick={handleSort}
                    />
                  ))}
                </tr>
              </thead>
              <tbody>
                {sortedTrafficData.map((source, index) => (
                  <tr key={index} className="border-b border-[#F3F4F6] hover:bg-[#F9FAFB]">
                    <td className="py-3 px-2 text-[14px] text-[#1A1A1A] font-medium">
                      <span className="block truncate max-w-[360px]" title={source.value || '-'}>
                        {source.value || '-'}
                      </span>
                      {source.aggregated && (
                        <span className="block text-[11px] text-[#9CA3AF] font-normal">
                          {translateInterfaceText("demais origens")}{source.aggregatedValues ? ` (${source.aggregatedValues})` : ''}
                        </span>
                      )}
                    </td>
                    <td className="py-3 px-2 text-[14px] text-[#1A1A1A] text-right font-medium">
                      {source.views?.toLocaleString(getInterfaceLanguage()) || 0}
                    </td>
                    <td className="py-3 px-2 text-[14px] text-[#1A1A1A] text-right font-medium">
                      {source.uniqueViews?.toLocaleString(getInterfaceLanguage()) || 0}
                    </td>
                    <td className="py-3 px-2 text-[14px] text-[#1A1A1A] text-right font-medium">
                      {source.plays?.toLocaleString(getInterfaceLanguage()) || 0}
                    </td>
                    <td className="py-3 px-2 text-[14px] text-[#1A1A1A] text-right font-medium">
                      {source.uniquePlays?.toLocaleString(getInterfaceLanguage()) || 0}
                    </td>
                    <td className="py-3 px-2 text-[14px] text-[#1A1A1A] text-right font-medium">
                      {source.clicks?.toLocaleString(getInterfaceLanguage()) || 0}
                    </td>
                    <td className="py-3 px-2 text-[14px] text-[#1A1A1A] text-right font-medium">
                      {source.engagement?.toFixed(2) || 0}%
                    </td>
                    <td className="py-3 px-2 text-[14px] text-[#1A1A1A] text-right font-medium">
                      {source.avgRetention?.toFixed(2) || 0}s
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Acessos sem UTM entram na tabela como 'Direto', mas o backend não gera
              curva de retenção para eles — então essa origem nunca está no gráfico. */}
          {temDiretoSemCurva && (
            <p className="text-[11px] text-[#9CA3AF] pt-3">
              {translateInterfaceText("Acessos sem UTM aparecem como “Direto” na tabela, mas não têm curva de retenção no gráfico.")}</p>
          )}
          </>
        ) : (
          <div className="text-center py-12">
            <ExternalLink className="h-12 w-12 text-[#E2E8FF] mx-auto mb-3" />
            <p className="text-[#6B7280] text-[14px]">
              {translateInterfaceText("Nenhuma fonte de tráfego detectada")}</p>
            <p className="text-[#9CA3AF] text-[12px] mt-1">
              {translateInterfaceText("Use parâmetros UTM nas suas URLs para rastrear a origem do tráfego")}</p>
          </div>
        )}
      </Card>
    </div>
  );
}
